import React, { useEffect, useState, useRef } from 'react';
import './NewLightStarPage.css';

interface NewLightStarPageProps {
  isVisible: boolean;
  scrollProgress?: number;
}

const NewLightStarPage: React.FC<NewLightStarPageProps> = ({ 
  isVisible, 
  scrollProgress = 0 
}) => {
  const [textScale, setTextScale] = useState(1);
  const [textOpacity, setTextOpacity] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const rect = container.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const containerCenter = rect.top + rect.height / 2;
      const viewportCenter = viewportHeight / 2;

      // 中央からの距離を計算
      const distanceFromCenter = Math.abs(containerCenter - viewportCenter);
      const maxDistance = viewportHeight / 2;
      
      // 中央に近づくほど大きくなる（1から3まで）
      const progress = Math.max(0, 1 - distanceFromCenter / maxDistance);
      const scale = 1 + progress * 2; // 1～3のスケール
      
      // スクロール位置に基づいてスケールを調整
      const scrollBasedScale = Math.min(scale, 1 + scrollProgress * 2);
      
      setTextScale(scrollBasedScale);
      
      // 透明度も調整（中央で最大、端で少し薄く）
      const opacity = 0.7 + progress * 0.3;
      setTextOpacity(opacity);
    };

    if (isVisible) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      handleScroll(); // 初期計算
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isVisible, scrollProgress]);

  return (
    <div 
      ref={containerRef}
      className={`new-light-star-container ${isVisible ? 'visible' : ''}`}
    >
      {/* 背景要素 */}
      <div className="star-background">
        {/* 星の背景 */}
        <div className="stars">
          {Array.from({ length: 100 }, (_, i) => (
            <div
              key={i}
              className="star"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 3}s`
              }}
            />
          ))}
        </div>

        {/* 光の粒子 */}
        <div className="light-particles">
          {Array.from({ length: 20 }, (_, i) => (
            <div
              key={i}
              className="light-particle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${3 + Math.random() * 4}s`
              }}
            />
          ))}
        </div>
      </div>

      {/* メインテキスト */}
      <div className="star-content">
        <h1 
          className="star-title"
          style={{
            transform: `scale(${textScale})`,
            opacity: textOpacity
          }}
        >
          <span className="star-text-part">新たな</span>
          <span className="star-text-part delay-1">光星</span>
        </h1>

        {/* サブテキスト */}
        <div 
          className="star-subtitle"
          style={{
            opacity: Math.max(0, 1 - (textScale - 1) * 0.5)
          }}
        >
          <p>未来への扉が今、開かれる</p>
        </div>
      </div>

      {/* 光の効果 */}
      <div 
        className="light-effect"
        style={{
          transform: `scale(${textScale * 0.8})`,
          opacity: (textScale - 1) * 0.5
        }}
      >
        <div className="light-ring"></div>
        <div className="light-ring ring-2"></div>
        <div className="light-ring ring-3"></div>
      </div>

      {/* スクロール進行表示 */}
      <div className="scroll-progress-indicator">
        <div className="progress-text">
          Scale: {textScale.toFixed(2)}x
        </div>
        <div className="progress-bar-container">
          <div 
            className="progress-bar-fill"
            style={{ width: `${((textScale - 1) / 2) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default NewLightStarPage;